




























import Vue from "vue";
import { add, differenceInDays, parse } from "date-fns";
import {
  SKILL_PRACTICE_TYPES,
  VUEX_LEARNING_HISTORY_ACTION_NAMES,
  VUEX_LEARNING_HISTORY_NAMESPACE,
  VUEX_ROOT_ACTION_NAMES,
  VUEX_ROOT_GETTER_NAMES
} from "@tt-app/constants";

import SkillCard from "@tt-app/components/skills/SkillCard.vue";
import FeedbackPopup from "@tt-app/components/feedback/FeedbackPopup.vue";

const { SAVE_SELECTED_SKILL_LEVEL_INDEXES } = VUEX_ROOT_ACTION_NAMES;
const { SELECTED_SKILL_LEVEL_INDEXES } = VUEX_ROOT_GETTER_NAMES;

interface LearningStats {
  registrationDate: string;
}

interface LearningStatLogs {
  date: string;
  total: number;
}

interface Data {
  chartStyles: {
    borderColor: string;
    borderWidth: number;
    fill: boolean;
    pointRadius: (chartData: any) => number;
  };
  dateFormat: string;
  feedbackParams?: {
    withFeedback: string;
    logId: string;
  };
  listeningType: number;
  pronunciationType: number;
  selectedProblemCount: number | null;
  vocabularyType: number;
  withFeedback: boolean;
}

interface Methods {
  createFillerLogs: (
    learningStats: LearningStats,
    learningStatLogs: LearningStatLogs[]
  ) => { x: Date; y: number }[];
  deriveChartData: (learningStatsId: number) => {};
  deriveLearnedWords: (learningStatsId: number) => {};
  handleFeedbackClose: () => void;
  levelSelectionChangeHandler: (selectedIndex: number) => void;
  problemCountChangeHandler: (problemCount: number) => void;
}

interface Computed {
  feedbackLogId?: string;
  learningStats: any;
  levels: {
    learned: number;
    possible: number;
    title: string;
  }[];
  vocabularyChartData: any;
  vocabularyLearnedWords: any;
  pronunciationChartData: any;
  pronunciationLearnedWords: any;
  showFeedbackForm: boolean;
  listeningChartData: any;
  listeningLearnedWords: any;
  vocabularyLevel: { [key: string]: number };
}

export default Vue.extend<Data, Methods, Computed, never>({
  name: "Skills",
  components: {
    FeedbackPopup,
    SkillCard
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      (vm as any).feedbackParams = to.params.feedbackParams;
    });
  },
  data() {
    return {
      chartStyles: {
        borderColor: "#becacd",
        borderWidth: 2,
        fill: false,
        pointRadius: ({ dataIndex, dataset }: any) => {
          if (dataset.data[dataIndex + 1] === undefined) {
            return 2;
          }
          return 0;
        }
      },
      dateFormat: "yyyy-MM-dd",
      feedbackParams: undefined,
      listeningType: SKILL_PRACTICE_TYPES.listening,
      pronunciationType: SKILL_PRACTICE_TYPES.pronunciation,
      selectedProblemCount: null,
      vocabularyType: SKILL_PRACTICE_TYPES.vocabulary,
      withFeedback: false
    };
  },
  created() {
    this.$store.dispatch(
      `${VUEX_LEARNING_HISTORY_NAMESPACE}/${VUEX_LEARNING_HISTORY_ACTION_NAMES.LEARNING_STATS_REQUEST}`,
      { exerciseType: SKILL_PRACTICE_TYPES.vocabulary }
    );

    this.$store.dispatch(
      `${VUEX_LEARNING_HISTORY_NAMESPACE}/${VUEX_LEARNING_HISTORY_ACTION_NAMES.LEARNING_STATS_REQUEST}`,
      { exerciseType: SKILL_PRACTICE_TYPES.listening }
    );

    this.$store.dispatch(
      `${VUEX_LEARNING_HISTORY_NAMESPACE}/${VUEX_LEARNING_HISTORY_ACTION_NAMES.LEARNING_STATS_REQUEST}`,
      { exerciseType: SKILL_PRACTICE_TYPES.pronunciation }
    );
  },
  computed: {
    feedbackLogId() {
      if (!this.feedbackParams) return;

      return this.feedbackParams.logId;
    },

    learningStats() {
      return this.$store.state.learningHistory.learningStats;
    },
    levels() {
      if (!this.learningStats[SKILL_PRACTICE_TYPES.vocabulary]) return [];

      const { schoolGrades } = this.learningStats[SKILL_PRACTICE_TYPES.vocabulary].learnedBadges;

      return schoolGrades.map((grade: any) => ({
        learned: grade.total,
        possible: grade.numWords,
        title: grade.schoolGrade
      }));
    },
    vocabularyLevel() {
      const selectedSkillLevelIndexes = this.$store.getters[SELECTED_SKILL_LEVEL_INDEXES];

      return selectedSkillLevelIndexes?.vocabulary || 0;
    },
    vocabularyChartData() {
      return this.deriveChartData(SKILL_PRACTICE_TYPES.vocabulary);
    },
    vocabularyLearnedWords() {
      return this.deriveLearnedWords(SKILL_PRACTICE_TYPES.vocabulary);
    },
    pronunciationChartData() {
      return this.deriveChartData(SKILL_PRACTICE_TYPES.pronunciation);
    },
    pronunciationLearnedWords() {
      return this.deriveLearnedWords(SKILL_PRACTICE_TYPES.pronunciation);
    },
    listeningChartData() {
      return this.deriveChartData(SKILL_PRACTICE_TYPES.listening);
    },
    listeningLearnedWords() {
      return this.deriveLearnedWords(SKILL_PRACTICE_TYPES.listening);
    },
    showFeedbackForm() {
      if (!this.feedbackParams) return false;

      return this.feedbackParams.withFeedback === "true";
    }
  },
  methods: {
    createFillerLogs(learningStats, learningStatLogs) {
      const currentDate = new Date();

      const registrationDateString = learningStats.registrationDate;
      const registrationDate = parse(registrationDateString, this.dateFormat, new Date());

      const possibleLogsSinceRegistration = differenceInDays(currentDate, registrationDate);

      const emptyLogsSinceRegistration =
        possibleLogsSinceRegistration - learningStatLogs.length + 1;

      const requiredFillerData = Math.min(Math.max(emptyLogsSinceRegistration, 0), 90);

      const emptyLogs: { x: Date; y: number }[] = [];
      for (let i = 0; i < requiredFillerData; i += 1) {
        const emptyDataDate = add(registrationDate, { days: i });

        const emptyDataPoint = {
          x: emptyDataDate,
          y: 0
        };

        emptyLogs.push(emptyDataPoint);
      }

      return emptyLogs;
    },
    deriveChartData(learningStatsId) {
      // case: no learning stats yet
      if (!this.learningStats[learningStatsId]) {
        return {};
      }

      const learningStats = this.learningStats[learningStatsId];
      const learningStatLogs = learningStats.learnedBadges.logs;

      const learningStatLogsWithDates = learningStatLogs.map(
        ({ date, total }: { date: string; total: string }) => {
          return {
            x: parse(date, this.dateFormat, new Date()),
            y: total
          };
        }
      );

      // case: learning stats logs can fill entire 90 days
      const enoughLogsForPast90Days = learningStatLogs.length >= 90;

      if (enoughLogsForPast90Days) {
        return {
          datasets: [
            {
              ...this.chartStyles,
              data: learningStatLogsWithDates
            }
          ]
        };
      }

      // case: need filler data for chart from registration date
      const emptyLogs = this.createFillerLogs(learningStats, learningStatLogs);

      const concatenatedEmptyLogsAndRealLogs = [...emptyLogs, ...learningStatLogsWithDates];

      return {
        datasets: [
          {
            ...this.chartStyles,
            data: concatenatedEmptyLogsAndRealLogs
          }
        ]
      };
    },
    deriveLearnedWords(learningStatsId) {
      if (!this.learningStats[learningStatsId]) {
        return undefined;
      }

      const learnedBadges = this.learningStats[learningStatsId].learnedBadges.logs;

      if (learnedBadges.length <= 0) return 0;

      return learnedBadges[learnedBadges.length - 1].total;
    },
    handleFeedbackClose() {
      if (!this.feedbackParams) return;
      this.feedbackParams.withFeedback = "false";
    },
    levelSelectionChangeHandler(selectedIndex) {
      this.$store.dispatch(SAVE_SELECTED_SKILL_LEVEL_INDEXES, {
        vocabulary: selectedIndex
      });
    },
    problemCountChangeHandler(problemCount) {
      this.selectedProblemCount = problemCount;
    }
  }
});
